/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classname from 'classnames';
import get from 'lodash/get';
import { action, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { Button, IModuleProps, INodeProps, Waiting } from '@msdyn365-commerce-modules/utilities';
import { PriceComponent } from '@msdyn365-commerce/components';
import { ICheckoutState } from '@msdyn365-commerce/global-state';
import { DeliveryOption } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { updateDeliverySpecificationAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';

import { IFarmlandsCheckoutDeliveryOptionsData } from './farmlands-checkout-delivery-options.data';
import { IFarmlandsCheckoutDeliveryOptionsProps } from './farmlands-checkout-delivery-options.props.autogenerated';
import DescriptionComponent from './components/farmlands-checkout-delivery-options.description';
import ErrorMessageComponent from './components/farmlands-checkout-delivery-options.error-message';
import ErrorTitleComponent from './components/farmlands-checkout-delivery-options.error-title';
import RadioButtonComponent from './components/farmlands-checkout-delivery-options.radio-button';
import { getDeliveryOptionsAsync } from '../../actions/farmlands-get-delivery-options-calls.action';
import { IFarmlandsDeliveryOptions } from './types';
export interface ICheckoutDeliveryOptionsModuleProps
    extends IFarmlandsCheckoutDeliveryOptionsProps<IFarmlandsCheckoutDeliveryOptionsData>,
        IModuleStateProps {}

export interface ICheckoutDeliveryOption {
    DeliveryOption: INodeProps;
    description?: React.ReactNode;
    price?: React.ReactNode;
}

export interface ICheckoutDeliveryOptionEdit extends ICheckoutDeliveryOption {
    code: string;
    radioButton?: React.ReactNode;
}

export interface ICheckoutDeliveryOptionsList {
    DeliveryOptionsList: INodeProps;
    list: ICheckoutDeliveryOptionEdit[];
}

export interface ICheckoutDeliveryOptionsError {
    Error: INodeProps;
    title: React.ReactNode;
    message: React.ReactNode;
}

export interface ICheckoutDeliveryOptionsViewState {
    isLoading: boolean;
    isError: boolean;
    isShowList: boolean;
    isShowSelected: boolean;
    isShowSaveButton: boolean;
    isShowEditButton: boolean;
    isShowCancelButton: boolean;
}

export interface ICheckoutDeliveryOptionsViewProps extends IFarmlandsCheckoutDeliveryOptionsProps<{}> {
    CheckoutDeliveryOptions: IModuleProps;
    selectedItemId: string;
    deliveryOptionsData: DeliveryOption[];
    isLoading: boolean;
    errorMessageData: string;
    viewState: ICheckoutDeliveryOptionsViewState;
    deliveryOptions?: ICheckoutDeliveryOptionsList;
    deliveryOptionSelected?: ICheckoutDeliveryOption;
    errorMessage?: ICheckoutDeliveryOptionsError;
    waiting?: React.ReactNode;
    saveButton?: React.ReactNode;
    editButton?: React.ReactNode;
    cancelButton?: React.ReactNode;
    onDeliveryOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;
    onSave(): void;
    onEdit(): void;
    onCancel(): void;
}

/**
 *
 * CheckoutDeliveryOptions component
 * @extends {React.Component<IFarmlandsCheckoutDeliveryOptionsProps<IFarmlandsCheckoutDeliveryOptionsData>>}
 */
// @ts-ignore
@withModuleState
@observer
class CheckoutDeliveryOptions extends React.Component<ICheckoutDeliveryOptionsModuleProps> {
    @observable private selectedItemId: string = '';
    @observable private deliveryOptions: IFarmlandsDeliveryOptions[] = [];
    @observable private isLoading: boolean = false;
    @observable private errorMessage?: string;
    private cartDeliveryMode?: string;

    public async componentDidMount(): Promise<void> {
        this.props.data.checkout.then(async () => {
            await this.init();
        });

        // @ts-ignore: Compiler not reconizing condition check for function params
        reaction(
            () => this.props.data.checkout.result && this.props.data.checkout.result.shippingAddress,
            async () => {
                await this.loadDeliveryOptions();
                this.props.moduleState.setIsCancellable(false);
            }
        );
        // @ts-ignore: Compiler not reconizing condition check for function params
        reaction(
            () => this.props.data.checkout.result && this.props.data.checkout.result.billingAddress,
            async () => {
                await this.loadDeliveryOptions();
                this.props.moduleState.setIsCancellable(false);
            }
        );
        // if the delivery method is set before, use the previously set value and close the module
        if (window.sessionStorage.getItem('selectedDeliveryMethod')) {
            this.selectedItemId = window.sessionStorage.getItem('selectedDeliveryMethod') || '';
            this.onReady();
        }
    }
    public render(): JSX.Element | null {
        if (!this.canShip()) {
            this.props.context.telemetry.error('Checkout delivery options content is empty, module wont render');
            return null;
        }

        const {
            moduleState: { isReady, isUpdating, hasError }
        } = this.props;
        const isError = !this.isLoading && hasError;
        const isShowList = !this.isLoading && !hasError && isUpdating;
        const isShowSelected = !this.isLoading && !hasError && isReady;

        const viewProps = {
            ...this.props,
            onDeliveryOptionChange: this.onDeliveryOptionChange,
            selectedItemId: this.selectedItemId,
            deliveryOptionsData: this.deliveryOptions,
            isLoading: this.isLoading,
            errorMessageData: this.errorMessage,
            viewState: {
                isLoading: this.isLoading,
                isError: isError,
                isShowList: isShowList,
                isShowSelected: isShowSelected,
                isShowSaveButton: isShowList && !this.props.moduleState.hasExternalSubmitGroup,
                isShowEditButton: isShowSelected && !this.props.moduleState.hasExternalSubmitGroup,
                isShowCancelButton:
                    isShowList && !this.props.moduleState.hasExternalSubmitGroup && !!this.getSavedDeliveryOption()
            },
            CheckoutDeliveryOptions: {
                moduleProps: this.props,
                className: classname('ms-checkout-delivery-options', this.props.config.className)
            },
            deliveryOptions: {
                // @ts-ignore
                DeliveryOptionsList: { className: 'ms-checkout-delivery-options__list' },
                list: this.getDeliveryOptionList()
            },
            deliveryOptionSelected: this.getDeliveryOptionSelected(),
            errorMessage: this.getErrorMessage(),
            waiting: <Waiting />,
            saveButton: this.getSaveButton(),
            editButton: this.getEditButton(),
            cancelButton: this.getCancelButton(),
            onSave: this.onSave,
            onEdit: this.onEdit,
            onCancel: this.onCancel
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    @action
    private init = async (): Promise<void> => {
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: this.canShip() ? 'updating' : 'disabled'
        });

        this.cartDeliveryMode = this.getSavedDeliveryOption();

        // await this.loadDeliveryOptions();
    };

    private canShip = (): boolean => {
        const { request } = this.props.context;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        if (this.props.data.checkout.result?.checkoutCart.cart.DeliveryMode) {
            return this.props.data.checkout.result?.checkoutCart.cart.DeliveryMode !== pickupDeliveryModeCode;
        }
        return false;
    };

    private isDeliveryModeExists = (deliveryMode: string | undefined): boolean => {
        if (deliveryMode) {
            if (
                this.deliveryOptions.find(
                    (deliveryOption) => deliveryOption[0]?.DeliveryModes?.[0]?.DeliveryMode === deliveryMode
                )
            ) {
                return true;
            }
        }
        return false;
    };

    private getDeliveryOptionSelected = (): ICheckoutDeliveryOption | null => {
        const selectedItem = this.deliveryOptions?.find(
            (deliveryOption) => deliveryOption.DeliveryModes[0].DeliveryMode === this.selectedItemId
        );
        if (!selectedItem) {
            this.props.context.telemetry.error('Checkout delivery options content is empty, module wont render');
            return null;
        }

        const { id, typeName, context } = this.props;
        const { priceFree } = this.props.resources;
        const price = get(selectedItem, 'ShippingChargeAmount', selectedItem.DeliveryModes[0].ChargeAmount);
        return {
            DeliveryOption: { className: 'ms-checkout-delivery-options__option-selected' },
            description: <DescriptionComponent {...{ description: selectedItem.DeliveryModes[0].ChargeDescription }} />,
            price: (
                <PriceComponent
                    id={id}
                    typeName={typeName}
                    context={context}
                    className='ms-checkout-delivery-options__price'
                    data={{ price: { CustomerContextualPrice: price } }}
                    freePriceText={priceFree}
                />
            )
        };
    };

    private getErrorMessage = (): ICheckoutDeliveryOptionsError => {
        const { errorMessageTitle } = this.props.resources;
        return {
            // @ts-ignore
            CheckoutDeliveryOptionsError: {
                tag: 'span',
                className: 'ms-address-form__error',
                role: 'alert',
                'aria-live': 'assertive'
            },
            title: <ErrorTitleComponent {...{ title: errorMessageTitle }} />,
            message: <ErrorMessageComponent {...{ message: this.errorMessage }} />
        };
    };

    private getDeliveryOptionList = (): ICheckoutDeliveryOptionEdit[] => {
        const count = this.deliveryOptions.length;
        const { priceFree } = this.props.resources;
        const { id, typeName, context } = this.props;

        return this.deliveryOptions?.map((deliveryMode: any, index: number) => {
            return {
                DeliveryOption: { className: 'ms-checkout-delivery-options__option' },
                code: deliveryMode.DeliveryMode || '',
                radioButton: (
                    <RadioButtonComponent
                        {...{
                            isChecked: deliveryMode.DeliveryModes[0].DeliveryMode === this.selectedItemId,
                            value: deliveryMode.DeliveryModes[0].DeliveryMode,
                            ariaSetSize: count,
                            ariaPosInSet: index + 1,
                            ariaLabel: `${deliveryMode.DeliveryModes[0].DeliveryMode} ${priceFree}`,
                            onChange: this.onDeliveryOptionChange
                        }}
                    />
                ),
                description: (
                    <DescriptionComponent
                        {...{
                            description: deliveryMode.DeliveryModes[0].ChargeDescription
                        }}
                    />
                ),
                price: (
                    <PriceComponent
                        id={id}
                        typeName={typeName}
                        context={context}
                        className='ms-checkout-delivery-options__price'
                        data={{
                            price: {
                                CustomerContextualPrice: get(
                                    deliveryMode,
                                    'ShippingChargeAmount',
                                    deliveryMode.DeliveryModes[0].ChargeAmount
                                )
                            }
                        }}
                        freePriceText={priceFree}
                    />
                )
            };
        });
    };

    private getSaveButton = (): React.ReactNode => {
        const { saveBtnLabel = 'Save' } = this.props.resources;
        return (
            <Button
                className='ms-checkout-delivery-options__btn-save'
                title={saveBtnLabel}
                color='primary'
                disabled={!this.selectedItemId}
                onClick={this.onSave}
            >
                {saveBtnLabel}
            </Button>
        );
    };

    private getEditButton = (): React.ReactNode => {
        const { editBtnLabel = 'Change' } = this.props.resources;
        return (
            <Button
                className='ms-checkout-delivery-options__btn-edit'
                title={editBtnLabel}
                color='primary'
                onClick={this.onEdit}
            >
                {editBtnLabel}
            </Button>
        );
    };

    private getCancelButton = (): React.ReactNode => {
        const { cancelBtnLabel = 'Cancel' } = this.props.resources;
        return (
            <Button
                className='ms-checkout-delivery-options__btn-cancel'
                title={cancelBtnLabel}
                color='secondary'
                onClick={this.onCancel}
            >
                {cancelBtnLabel}
            </Button>
        );
    };

    private setError = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.errorMessage = errorMessage;
    };

    private clearError = (): void => {
        this.props.moduleState.setHasError(false);
        this.errorMessage = undefined;
    };

    private onSave = (): void => {
        this.onSubmit();
    };

    private onEdit = (): void => {
        this.onUpdating();
    };

    private onSubmit = (): void => {
        window.sessionStorage.setItem('selectedDeliveryMethod', this.selectedItemId);
        // tslint:disable-next-line:no-floating-promises
        this.updateDeliverySpecification(this.selectedItemId);
    };

    private onCancel = (): void => {
        // Reset to the saved value
        const savedDeliveryOption = this.getSavedDeliveryOption();
        if (savedDeliveryOption) {
            this.selectedItemId = savedDeliveryOption;
            this.onReady();
        } else {
            this.onUpdating();
        }
    };

    private getSavedDeliveryOption = (): string | undefined => {
        const checkoutCartDeliveryMode = this.props.data.checkout.result?.checkoutCart.cart.DeliveryMode;
        return checkoutCartDeliveryMode && checkoutCartDeliveryMode;
    };

    private updateDeliverySpecification = async (deliveryModeId: string): Promise<void> => {
        if (deliveryModeId.trim() === '') {
            return;
        }

        const { resources } = this.props;
        const checkoutState = this.props.data.checkout.result as ICheckoutState;
        const updateDeliverySpecificationInput = {
            DeliveryModeId: deliveryModeId,
            DeliveryPreferenceTypeValue: 1,
            DeliveryAddress: checkoutState.shippingAddress
        };
        this.clearError();
        this.onPending();

        await updateDeliverySpecificationAsync(
            { callerContext: this.props.context.actionContext },
            checkoutState.checkoutCart.cart.Id,
            updateDeliverySpecificationInput
        )
            .then(async (response: any) => {
                if (response.status === 'FAILED') {
                    this.setError(resources.genericErrorMessage);
                } else {
                    // TODO: wrapping updateDeliverySpecification call with _doCartOperationWithRetry is better approach
                    await checkoutState.checkoutCart.refreshCart({});
                    this.onReady();
                    this.props.moduleState.setIsCancellable(true);
                }
            })
            .catch(() => {
                this.setError(resources.genericErrorMessage);
            });
    };

    private getDeliveryOptions = async (): Promise<void> => {
        if (!this.props.data.checkout.result) {
            return;
        }

        const { context, resources } = this.props;
        const cartState = this.props.data.checkout.result?.checkoutCart;
        if (context) {
            this.isLoading = true;
            this.deliveryOptions = [];
            this.selectedItemId = '';
            this.clearError();

            try {
                // let originalDeliveryMode: string = '';
                // first store the delivery mode, so that we can restore the shipping information.
                // originalDeliveryMode = cartState.cart.DeliveryMode || '';

                // clear the delivery information on retail server, so GetDeliveryOptions charge calculation works correctly
                const cart = cartState.cart;
                await cartState.clearCartLinesDeliveryInformation({ cart });

                const checkoutCartCookieId = this.props.context.actionContext.requestContext.cookies
                    ?.getCheckoutCartCookie()
                    .substring(2);
                // FBHGetDeliveryOptionsByCart API call
                await this.props.data.checkout.result?.checkoutCart.refreshCart({});
                const response = await getDeliveryOptionsAsync({
                    context: { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                    cartId: checkoutCartCookieId
                });

                if (response) {
                    const allDeliveryModes = await Promise.all(response.DeliveryOptions);
                    this.deliveryOptions = this.removePickUpFromDeliveryArray(allDeliveryModes);
                }

                // if (cartState.cart.DeliveryMode) {
                //     await this.updateDeliverySpecification(originalDeliveryMode);
                // }
            } catch (error) {
                this.setError(resources.genericErrorMessage);
            }

            if (this.deliveryOptions.length === 0) {
                this.setError(resources.deliveryOptionsNotFound);
            } else {
                if (
                    this.cartDeliveryMode &&
                    this.isDeliveryModeExists(this.cartDeliveryMode) &&
                    !this.props.data.checkout.result?.checkoutCart.cart.ShippingAddress
                ) {
                    this.selectedItemId = this.cartDeliveryMode;
                    this.cartDeliveryMode = undefined;
                    this.onReady();
                } else {
                    this.selectedItemId =
                        this.deliveryOptions.length > 0
                            ? this.deliveryOptions?.[0]?.DeliveryModes?.[0]?.DeliveryMode || ''
                            : ''; // By default, select first delivery
                }
            }

            this.isLoading = false;
        }
    };

    private removePickUpFromDeliveryArray = (response: any) => {
        return response.filter((deliveryMethod: any) => deliveryMethod.DeliveryType === 'Delivery');
    };

    private loadDeliveryOptions = async (): Promise<void> => {
        if (this.canShip()) {
            return this.getDeliveryOptions();
        }
    };

    private onDeliveryOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const deliveryModeId = event.currentTarget.value;
        this.selectedItemId = deliveryModeId;
    };

    private onReady = () => {
        this.props.moduleState.onReady();
    };

    private onUpdating = () => {
        this.props.moduleState.onUpdating();
    };

    private onPending = () => {
        this.props.moduleState.onPending();
    };
}

export default CheckoutDeliveryOptions;
